import React from 'react';
import styled, { keyframes } from 'styled-components';
import Img from 'gatsby-image';
import { Heading, mediaQueries, spacing, fonts, fontSizes } from '../components/Styling';

function convertRemToPixels(rem) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

const GridLabel = styled.span`
  display: inline-block;
  float: ${props => props.right ? 'right' : 'initial'};
  font-size: ${fontSizes[1]};
  ${mediaQueries.medium`
      font-size: ${fontSizes[2]};
  `};
`;

const GridTitle = styled.h2`
  font-size: ${fontSizes[3]};
  ${mediaQueries.medium`
      font-size: ${fontSizes[3]};
  `};
`;

const GridDescription = styled.p`
  opacity: ${props => props.show || props.active ? 1 : 0};
  transition: opacity 0.3s;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Inner = styled.div`
  animation-duration: 0.5s;
  animation-name: ${props => props.animationName};
  animation-fill-mode: forwards;
  backface-visibility: hidden;
  background-color: rgba(0, 0, 0, 0.1);
  color: white;
  height: 100%;
  left: 0;
  padding: calc(2vw + ${spacing[2]});
  position: absolute;
  transform: ${props => props.active ? 'translateY(-100%)' : 'initial'};
  transform-origin: left bottom;
  top: 0;
  width: 100%;

  ${mediaQueries.medium`
    padding: ${spacing[4]};
    transform: ${props => props.active ? 'translateY(-100%) scale(.75)' : 'scale(1)'};
    width: ${props => props.active ? 'calc(100% + 100% / 3)' : '100%'};
  `};
  ${mediaQueries.large`
    padding: ${spacing[4]};
    transform: ${props => props.active ? 'translateY(-100%) scale(.5)' : 'scale(1)'};
    width: ${props => props.active ? 'calc(200%)' : '100%'};
  `};
`;

const StyledVideo = styled.video`
  backface-visibility: hidden;
  height: calc(100% + 2px);
  left: -1px;
  object-fit: cover;
  opacity: ${props => props.hide ? 0 : 1};
  position: absolute;
  top: -1px;
  transition: opacity 0.2s;
  width: calc(100% + 2px);
  z-index: 1;
`;

const Poster = styled(Img)`
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
`;

const PosterWrapper = styled.div`
  height: 100%;
  left: 0;
  opacity: ${props => props.hide ? 0 : 1};
  position: absolute;
  top: 0;
  width: 100%;
  transition: opacity 0.2s;
`;

class Video extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true };
    this.pause = this.pause.bind(this);
    this.play = this.play.bind(this);
    this.sourceLoaded = false;
    this.hasSource = null;
  }

  pause = () => {
    this.video.pause();
  };

  play = () => {
    this.video.play();
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.hover) {
      if (!this.sourceLoaded) {
        this.sourceLoaded = true;
        this.refs.sourceRef.src = this.props.video;
        this.video.load();
        this.video.addEventListener('canplay', this.play);
        this.video.addEventListener('canplay', this.props.didLoad);
        return;
      }
      this.play();
    } else if (!nextProps.hover) {
      this.video.removeEventListener('canplay', this.play);
      this.pause();
    }
  }

  render() {
    return (
      <StyledVideo show={this.props.show} loop muted innerRef={e => this.video = e}>
        <source ref="sourceRef" src="" type="video/mp4" />
      </StyledVideo>
    );
  }
}

const VideoWrapper = styled.div`
  height: 0;
  left: 0;
  overflow: hidden;
  padding-top: 56.25%;
  position: absolute;
  top: 0;
  width: 100%;
`;

const ContentWrapper = styled.div`
  height: 0;
  opacity: ${props => props.active ? 0 : 1};
  padding-top: 56.25%;
  transition: ${props => props.active ? 'opacity 0s .5s, transform .4s !important' : 'transform .18s'};
  transform: ${props =>
    props.active
      ? 'translateX(calc(-' +
        props.rect.left +
        'px + 50vw - 50%)) translateY(calc(-1 * ' +
        props.rect.top +
        'px + 50vh - 50%)) translateZ(200px) scale(' +
        (window.innerWidth <= convertRemToPixels(60)
          ? window.innerWidth / props.rect.width
          : convertRemToPixels(60) / props.rect.width) +
        ') !important'
      : 'translateZ(0px) scale(1);'};
  transition-timing-function: cubic-bezier(0.86, 0, 0.07, 1);

  ${mediaQueries.medium`
    padding-top: '56.25%';
  `};

  &:after {
    background-color: white;
    content: '';
    height: 100%;
    left: 0;
    opacity: ${props => props.fade ? 0.4 : 0};
    position: absolute;
    top: 0;
    transition: opacity 0.4s;
    transform: translateZ(0);
    width: 100%;
  }

  &:hover {
    transition: transform 0.2s 0.05s;
    transform: translateZ(200px) scale(1.05);
    :after {
      opacity: 0 !important;
    }
  }
`;

class UnstyledGridElement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hover: false,
      boundingClientRect: null,
      animationName: fadeIn,
      didLoad: false
    };
    this.setHover = this.setHover.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
  }

  restartAnimation() {
    this.setState({ animationName: 'none' });
    setTimeout(() => {
      this.setState({ animationName: fadeIn });
    }, 0);
  }

  componentDidMount() {
    this.updatePosition();
  }

  updatePosition() {
    let rect = this.elementRect.getBoundingClientRect();
    this.setState({ boundingClientRect: rect });
  }

  setHover(value) {
    this.setState({ hover: value });
    this.props.toggleHover(value);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.active !== this.props.active) {
      this.updatePosition();
      this.restartAnimation();
    }
  }

  render() {
    return (
      <div
        className={this.props.className}
        data={this.props.data}
        onMouseEnter={() => this.setHover(true)}
        onMouseLeave={() => this.setHover(false)}
        onClick={() => this.props.toggleActive(this.props.data)}
        ref={e => this.elementRect = e}
      >
        <ContentWrapper
          data={this.props.data}
          fade={this.props.fade}
          active={this.props.active}
          rect={this.state.boundingClientRect}
        >
          <VideoWrapper rect={this.state.boundingClientRect}>
            <Video
              didLoad={() => {
                this.setState({ didLoad: true });
              }}
              hide={!this.state.didLoad}
              active={this.props.active}
              hover={this.state.hover}
              video={this.props.data.videoShort}
            />
            <PosterWrapper>
              <Poster
                sizes={
                  this.props.images.filter(
                    image =>
                      image.node.name ===
                      this.props.data.image.match(/(\/media\/)(.*)(\.jpg|.png|.gif|.jpeg|.tiff|.webp)/)[2]
                  )[0].node.childImageSharp.sizes
                }
              />
            </PosterWrapper>
          </VideoWrapper>
          {!this.props.active && 
            <Inner active={this.props.active} animationName={this.state.posterDidLoad ? this.state.animationName : ''}>
              <GridLabel> {this.props.data.year}&nbsp; </GridLabel>
              <GridLabel right={!this.props.active}> {this.props.data.client} </GridLabel>
              <GridTitle> {this.props.data.title} </GridTitle>
              <GridDescription active={this.props.active} show={this.state.hover}>
                {' '}
                {this.props.data.descriptionShort}{' '}
              </GridDescription>
              {this.props.active && <Close />}
            </Inner>
          }
        </ContentWrapper>
      </div>
    );
  }
}

export const GridElement = styled(UnstyledGridElement)`
  animation-duration: 0.25s;
  animation-name: ${fadeIn};
  flex: 0 0 100%;
  order: ${props => props.data.priority ? props.data.priority : 1};
  position: relative;
  transition: ${props => props.active ? 'none' : 'all 0.2s'};

  ${mediaQueries.medium`
    flex-basis: ${props => props.data.fullWidth ? '100%' : '50%'};
    `};
  ${mediaQueries.large`
    flex-basis: ${props => props.data.fullWidth ? 'calc(100% / 1.5)' : 'calc(100%/3)'};
  `};
`;



// WEBPACK FOOTER //
// ./src/components/GridElement.js